import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function useFadeIn(delay = 0) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let timer;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timer = setTimeout(() => setVisible(true), delay);
          observer.unobserve(el);
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -32px 0px' }
    );
    observer.observe(el);
    return () => { observer.unobserve(el); clearTimeout(timer); };
  }, [delay]);
  return [ref, visible];
}

const S = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };

function IconArrow({ className = 'w-4 h-4' }) {
  return <svg viewBox="0 0 24 24" className={className} {...S}><path d="M5 12h14M12 5l7 7-7 7" /></svg>;
}
function IconCheckCircle() {
  return <svg viewBox="0 0 24 24" className="w-6 h-6" {...S}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>;
}
function IconMonitor({ className = 'w-7 h-7' }) {
  return <svg viewBox="0 0 24 24" className={className} {...S}><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>;
}
function IconPhone({ className = 'w-6 h-6' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...S}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.95a16 16 0 0 0 6 6l1.06-.94a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}
function IconZap({ className = 'w-6 h-6' }) {
  return <svg viewBox="0 0 24 24" className={className} {...S}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>;
}

const TRUST = [
  {
    Icon: IconMonitor,
    title: 'Clean, Professional Design',
    desc: 'Every site is built to match your brand and make the kind of first impression that wins clients.',
  },
  {
    Icon: IconPhone,
    title: 'Built to Drive Results',
    desc: 'Our page are designed to convert visitors into real leads, inquiries, and revenue.',
  },
  {
    Icon: IconZap,
    title: 'Zero Jargon, We Handle It All',
    desc: 'You focus on your business. We handle the design, hosting, and setup end-to-end.',
  },
];

const HERO_IMAGES = ['/project1.webp', '/project2.webp', '/project3.webp'];
const SLIDE_INTERVAL = 8000;

export default function SalesHome() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [error, setError]               = useState(null);
  const [scrolled, setScrolled]         = useState(false);
  const [activeHeroBg, setActiveHeroBg] = useState(0);

  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 40);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);

  useEffect(() => {
    const id = setInterval(
      () => setActiveHeroBg((p) => (p + 1) % HERO_IMAGES.length),
      SLIDE_INTERVAL,
    );
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!showThankYou) return;
    const id = setTimeout(() => setShowThankYou(false), 4000);
    return () => clearTimeout(id);
  }, [showThankYou]);

  const [pathRef,    pathVisible]    = useFadeIn(0);
  const [trustRef,   trustVisible]   = useFadeIn(0);
  const [contactRef, contactVisible] = useFadeIn(0);

  const fc = (v) =>
    `transition-all duration-700 ease-out ${v ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    const form = e.target;
    const fd   = new FormData(form);
    fd.append('access_key', process.env.REACT_APP_WEB3FORMS_KEY);
    try {
      const res  = await fetch('https://api.web3forms.com/submit', { method: 'POST', body: fd });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.success) { form.reset(); setShowThankYou(true); }
      else setError('Something went wrong. Please try again.');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-white text-slate-900 selection:bg-emerald-200/60">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ease-in-out ${
          scrolled
            ? 'bg-white/75 border-b border-black/[0.07] shadow-sm shadow-black/[0.04]'
            : 'bg-transparent border-b border-white/[0.06]'
        }`}
        style={{ backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)' }}
      >
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <svg
              viewBox="0 0 512 512"
              className={`h-10 w-10 group-hover:scale-105 transition-all duration-500 ${scrolled ? 'text-slate-900' : 'text-white'}`}
              fill="none" stroke="currentColor" strokeWidth={38} strokeLinecap="round" strokeLinejoin="round"
            >
              <g transform="translate(256,256)">
                <circle cx="0" cy="0" r="140" />
                <path d="M -50 105 L -50 -105" />
                <path d="M -50 -105 L 10 -105 A 60 60 0 0 1 10 15 L -50 15" />
              </g>
            </svg>
            <span className={`text-lg font-semibold tracking-tight transition-colors duration-500 ${scrolled ? 'text-slate-900' : 'text-white'}`}>
              Peronyx
            </span>
          </Link>
          <Link
            to="/start"
            className={`inline-flex items-center gap-1.5 rounded-xl px-5 py-2 text-sm font-semibold transition-all duration-300 ${
              scrolled
                ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-md shadow-emerald-900/20'
                : 'border border-white/22 text-white hover:bg-white/10'
            }`}
          >
            Get Started
            <span className="inline-flex"><IconArrow /></span>
          </Link>
        </div>
      </header>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section id="home" className="relative min-h-[88vh] flex items-center overflow-hidden bg-slate-950">
        {HERO_IMAGES.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            role="presentation"
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[2500ms] ease-out ${
              i === activeHeroBg ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-slate-950/80" />
        <div className="absolute inset-0 hero-color-drift" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/65 via-slate-950/20 to-transparent" />
        <div className="hero-blob absolute -top-32 left-1/2 h-96 w-[44rem] rounded-full blur-3xl bg-gradient-to-tr from-emerald-500 to-sky-400 pointer-events-none" />

        <div className="relative z-10 mx-auto max-w-6xl px-6 py-32 w-full">
          <p className="hero-fade-up text-xs font-semibold tracking-[0.28em] text-emerald-400 uppercase" style={{ animationDelay: '60ms' }}>
            Design · Build · Convert
          </p>
          <h1 className="mt-4 text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.06] text-white max-w-3xl">
            <span className="inline-flex flex-wrap gap-x-[0.3em]">
              {['Build', 'an', 'Online', 'Presence'].map((word, i) => (
                <span key={word} className="word-rise" style={{ animationDelay: `${160 + i * 100}ms` }}>{word}</span>
              ))}
            </span>
            <span className="word-rise block mt-1" style={{ animationDelay: '580ms' }}>That Gets You Found</span>
            <span className="word-rise-breathe block mt-1">
              <span className="shimmer-text">And Wins You More Business</span>
            </span>
          </h1>
          <p className="hero-fade-up mt-6 max-w-xl text-slate-300 text-lg leading-relaxed" style={{ animationDelay: '860ms' }}>
              We builds clean, professional websites for businesses that want to build more trust, a better online presence and a simple way for customers to reach you.
          </p>
          <div className="hero-fade-up mt-10 flex flex-wrap gap-3" style={{ animationDelay: '1020ms' }}>
            <Link
              to="/start"
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-7 py-3.5 text-white text-sm font-semibold hover:bg-emerald-600 active:scale-[0.97] transition-all duration-150 shadow-lg shadow-emerald-900/30"
            >
              Book a Consultation
              <span className="inline-flex"><IconArrow /></span>
            </Link>
            <Link
              to="/review"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-7 py-3.5 text-white text-sm font-medium hover:bg-white/10 active:scale-[0.97] transition-all duration-150"
            >
              Free Website Review
              <span className="inline-flex"><IconArrow /></span>
            </Link>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveHeroBg(i)}
              aria-label={`Show slide ${i + 1}`}
              className={`h-1 rounded-full transition-all duration-500 ${
                i === activeHeroBg ? 'w-8 bg-emerald-400' : 'w-2 bg-white/25 hover:bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* ── Single Path Card ────────────────────────────────────────────────── */}
      <section id="paths" className="py-24 bg-[#f5f5f7]">
        <div ref={pathRef} className={`mx-auto max-w-6xl px-6 ${fc(pathVisible)}`}>
          <div className="text-center mb-12">
            <p className="text-xs font-semibold tracking-[0.2em] text-emerald-600 uppercase mb-3">Get started</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Ready for a Website That Works?</h2>
            <p className="mt-3 text-slate-500 text-lg max-w-lg mx-auto">
              Tell us about your business and we'll show you exactly what your site could look like.
            </p>
          </div>
          <div className="flex justify-center">
            <Link
              to="/start"
              className="group inline-flex items-center gap-3 rounded-2xl bg-emerald-500 px-10 py-5 text-white text-lg font-semibold hover:bg-emerald-600 active:scale-[0.97] transition-all duration-150 shadow-xl shadow-emerald-900/25"
            >
              Get Started
              <span className="group-hover:translate-x-1 transition-transform duration-200 inline-flex">
                <IconArrow className="w-5 h-5" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Trust Strip ─────────────────────────────────────────────────────── */}
      <section className="py-20 bg-white border-b border-slate-100">
        <div ref={trustRef} className={`mx-auto max-w-6xl px-6 ${fc(trustVisible)}`}>
          <div className="grid md:grid-cols-3 gap-10">
            {TRUST.map(({ Icon, title, desc }) => (
              <div key={title} className="flex flex-col items-center text-center px-4">
                <div className="mb-4 h-12 w-12 rounded-xl bg-emerald-50 grid place-items-center text-emerald-600">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-base font-bold tracking-tight text-slate-900 mb-1.5">{title}</h3>
                <p className="text-[14px] text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact ────────────────────────────────────────────────────────── */}
      <section id="contact" className="relative overflow-hidden bg-slate-950 text-white py-28">
        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 w-64 md:w-[40rem] h-64 blur-3xl opacity-10 bg-emerald-500 rounded-full" />
        <div className="relative mx-auto max-w-xl px-6">
          <div ref={contactRef} className={fc(contactVisible)}>
            <div className="text-center mb-12">
              <p className="text-xs font-semibold tracking-[0.28em] text-emerald-400 uppercase mb-4">Get in touch</p>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight">Have questions?</h2>
              <p className="mt-4 text-slate-400 text-lg">Send us a message and we'll get back to you.</p>
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              <input type="checkbox" name="botcheck" className="hidden" aria-hidden="true" />
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">Name</span>
                  <input
                    name="name"
                    required
                    placeholder="Your name"
                    className="mt-2 w-full rounded-xl bg-white/[0.06] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">Email</span>
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="you@company.com"
                    className="mt-2 w-full rounded-xl bg-white/[0.06] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">
                  Phone <span className="normal-case tracking-normal font-normal text-slate-600">(optional)</span>
                </span>
                <input
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="(555) 000-0000"
                  className="mt-2 w-full rounded-xl bg-white/[0.06] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">Message</span>
                <textarea
                  name="message"
                  required
                  rows={5}
                  placeholder="Tell us about your business and what you're looking for."
                  className="mt-2 w-full rounded-xl bg-white/[0.06] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all resize-none"
                />
              </label>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-sweep w-full rounded-xl border border-emerald-500 text-emerald-400 hover:text-white px-5 py-3.5 text-sm font-semibold active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
              >
                {isSubmitting ? 'Sending…' : 'Send Message'}
              </button>
              {error && <p className="text-xs text-red-400 text-center">{error}</p>}
            </form>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-600">
              <a href="mailto:peronyxtech@gmail.com" className="hover:text-slate-300 transition-colors">
                peronyxtech@gmail.com
              </a>
              <span>·</span>
              <span>Mon – Fri, 8am – 6pm CT</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-800 bg-slate-950 text-slate-400">
        <div className="mx-auto max-w-6xl px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-6 text-sm">
          <div className="flex items-center gap-2.5">
            <svg viewBox="0 0 512 512" className="h-8 w-8 text-slate-200" fill="none" stroke="currentColor" strokeWidth={28} strokeLinecap="round" strokeLinejoin="round">
              <g transform="translate(256,256)">
                <circle cx="0" cy="0" r="140" />
                <path d="M -50 105 L -50 -105" />
                <path d="M -50 -105 L 10 -105 A 60 60 0 0 1 10 15 L -50 15" />
              </g>
            </svg>
            <span className="font-medium text-slate-200">Peronyx</span>
            <span className="text-slate-700">·</span>
            <span className="text-slate-500">Build well, ship fast, stay secure.</span>
          </div>
          <span className="text-slate-600 text-xs">© 2026 Peronyx</span>
          <Link to="/start" className="hover:text-slate-200 transition-colors duration-150">Get Started</Link>
        </div>
      </footer>

      {/* ── Thank-you modal ─────────────────────────────────────────────────── */}
      {showThankYou && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 backdrop-blur-sm backdrop-enter">
          <div className="mx-4 max-w-sm w-full rounded-2xl bg-white p-8 shadow-2xl text-center border border-slate-200 modal-enter">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <IconCheckCircle />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Message sent!</h3>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">
              Thanks for reaching out. We'll get back to you as soon as possible.
            </p>
            <button
              onClick={() => setShowThankYou(false)}
              className="mt-6 inline-flex items-center rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-800 active:scale-[0.97] transition-all duration-150"
            >
              Close
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
